import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import Button from 'components_new/atoms/Button'
import Stack from 'components_new/atoms/Stack'
import Tooltip from 'components_new/atoms/Tooltip'

import {
  INITIAL_FORM_STATE,
  isCompleteExpression,
  fieldRowId,
  MetricFormState,
  missingFields,
  missingFieldsSentence,
  toFormState,
  toTextsBody
} from 'components_new/organisms/internal/MetricStudio/utils'
import MetricForm from 'components_new/templates/internal/MetricStudio/MetricForm'
import PageLayout from 'components_new/templates/PageLayout'

import { useDerivedMetricActions } from 'redux/actions/internal/DerivedMetrics'
import { useInternalDerivedMetrics } from 'redux/hooks/internal/DerivedMetrics'

const LIST_PATH = '/internal/metric-studio'

/**
 * One metric, read as it is stored and changed in an editing mode.
 *
 * A full page rather than a dialog: the definition is the point of the page,
 * and it needs room for two operand cards side by side plus a preview.
 */
const MetricStudioEditor = () => {
  const { id } = useParams<{ id?: string }>()
  const history = useHistory()
  const location = useLocation<{ edit?: boolean } | undefined>()

  const { data, fetched, options, optionsFetched, preview, previewing } =
    useInternalDerivedMetrics()

  const {
    tryGetDerivedMetrics,
    tryGetDerivedMetricOptions,
    tryPreviewDerivedMetric,
    tryCreateDerivedMetric,
    tryUpdateDerivedMetric
  } = useDerivedMetricActions()

  const [state, setState] = useState<MetricFormState>(INITIAL_FORM_STATE)
  const [previewCustomerId, setPreviewCustomerId] = useState<string | null>(
    null
  )
  // A metric that exists is read first, unless the pencil in the listing is
  // what opened it. A new one has nothing to read.
  const [editRequested, setEdit] = useState(!id || !!location.state?.edit)
  const edit = editRequested && options.editable
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!fetched) {
      tryGetDerivedMetrics()
    }
    if (!optionsFetched) {
      tryGetDerivedMetricOptions()
    }
  }, [fetched, optionsFetched])

  // The metric is loaded with the list, so the form fills in once it arrives.
  const metric = id ? data[id] ?? null : null

  useEffect(() => {
    if (metric) {
      setState(toFormState(metric))
    }
  }, [metric])

  const runnable =
    !!state.fact_table_id && isCompleteExpression(state.expression)

  // The CMS keys its entries on the slug, so two metrics cannot share one.
  const slug = state.texts.slug.trim()

  const missing = missingFields(state)

  const slugTaken =
    !!slug &&
    Object.values(data).some(
      (item) => item.id !== id && item.texts?.slug === slug
    )

  // Preview whenever the definition is runnable and a customer is picked —
  // the number is the whole reason for the step, so it should not need a
  // separate click.
  useEffect(() => {
    if (!runnable || !previewCustomerId) {
      return
    }

    tryPreviewDerivedMetric(
      {
        customer_id: previewCustomerId,
        unit: state.unit,
        expression: state.expression
      },
      () => undefined
    )
  }, [previewCustomerId, runnable, state.expression, state.unit])

  const submit = () => {
    if (!state.category) {
      return
    }

    setSaving(true)
    setError(null)
    setWarning(null)

    const body = {
      category: state.category,
      unit: state.unit,
      increase_is_positive: state.increase_is_positive,
      expression: state.expression,
      texts: toTextsBody(state.texts)
    }

    const callback = (
      saveError: string | null,
      _metric: unknown,
      textsSynced?: boolean
    ) => {
      setSaving(false)

      if (saveError) {
        setError(saveError)

        return
      }

      // The metric is saved either way. Staying on the page is the only way
      // to retry the texts, and saving again is what retries them.
      if (options.writes_texts && !textsSynced) {
        setWarning(
          'Mätetalet är sparat, men texterna kom inte in i innehålls' +
            'systemet. Spara igen för att försöka på nytt.'
        )

        return
      }

      history.push(LIST_PATH)
    }

    if (id) {
      tryUpdateDerivedMetric(id, body, callback)

      return
    }

    tryCreateDerivedMetric(body, callback)
  }

  const saveBlockedBy = slugTaken
    ? 'Slugen används redan av ett annat mätetal.'
    : missing.length > 0
      ? missingFieldsSentence(missing)
      : ''

  // Leaving the editing mode throws away the changes, so an existing metric
  // is read again from what was saved.
  const cancel = () => {
    if (!id) {
      history.push(LIST_PATH)

      return
    }

    if (metric) {
      setState(toFormState(metric))
    }

    setError(null)
    setWarning(null)
    setEdit(false)
  }

  return (
    <PageLayout
      content={{ editMode: { active: edit }, fullWidth: true }}
      topbar={{
        actions: edit ? (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {missing.length > 0 ? (
              <Button
                onClick={() =>
                  document
                    .getElementById(fieldRowId(missing[0]))
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
                size="small"
                variant="text"
              >
                {missing.length === 1
                  ? '1 fält saknas'
                  : `${missing.length} fält saknas`}
              </Button>
            ) : null}
            <Button onClick={cancel} variant="text">
              Avbryt
            </Button>
            <Tooltip title={saveBlockedBy}>
              <span>
                <Button
                  disabled={missing.length > 0 || slugTaken || saving}
                  onClick={submit}
                >
                  {id ? 'Spara' : 'Skapa mätetal'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ) : options.editable ? (
          <Button onClick={() => setEdit(true)} variant="text">
            Ändra
          </Button>
        ) : null,
        breadcrumbs: [
          { title: 'Admin' },
          { title: 'Mätetalsstudion', href: LIST_PATH },
          { title: id ? 'Mätetal' : 'Nytt mätetal' }
        ]
      }}
      variant="feed"
    >
      <MetricForm
        edit={edit}
        error={error}
        isEdit={!!id}
        missing={missing}
        onChange={setState}
        onSelectPreviewCustomer={setPreviewCustomerId}
        options={options}
        preview={preview}
        previewCustomerId={previewCustomerId}
        previewing={previewing}
        slugTaken={slugTaken}
        state={state}
        warning={warning}
      />
    </PageLayout>
  )
}

export default MetricStudioEditor
